import type { EventMap, PhxReply, SystemEventMap, WireMessage } from "./types.ts";

export class Channel<TEvents extends EventMap = Record<string, unknown>> {
  private handlers = new Map<keyof (TEvents & SystemEventMap) & string, Set<(p: any) => void>>();
  private pendingRefs = new Map<string, (reply: PhxReply<unknown>) => void>();
  private refCounter = 0;
  private lastJoin?: { token: string };

  constructor(
    private socket: { send: (msg: WireMessage) => void },
    private topic: string,
  ) {}

  /** Join channel with JWT token */
  async join(token: string): Promise<void> {
    this.lastJoin = { token };
    const ref = this.makeRef();
    return new Promise((resolve, reject) => {
      this.pendingRefs.set(ref, (reply) => {
        if (reply.status === "ok") resolve();
        else reject(new Error("Join failed"));
      });
      this.push("phx_join", { token } as TEvents["phx_join"], ref);
    });
  }

  /** Rejoin channel (used after reconnect) */
  async rejoin(): Promise<void> {
    if (this.lastJoin) {
      try {
        await this.join(this.lastJoin.token);
      } catch {
        // Swallow; reconnect loop will retry
      }
    }
  }

  /** Leave channel */
  async leave(): Promise<void> {
    const ref = this.makeRef();
    return new Promise((resolve) => {
      this.pendingRefs.set(ref, () => resolve());
      this.push("phx_leave", {} as TEvents["phx_leave"], ref);
    });
  }

  /** Send event to channel */
  push<E extends keyof TEvents & string>(event: E, payload: TEvents[E], ref?: string): void {
    const msg: WireMessage = {
      topic: this.topic,
      event,
      ref: ref ?? null,
      payload,
    };
    this.socket.send(msg);
  }

  /** Send event and wait for ack with timeout */
  pushAck<E extends keyof TEvents & string, R = unknown>(
    event: E,
    payload: TEvents[E],
    timeoutMs = 5000,
  ): Promise<PhxReply<R>> {
    const ref = this.makeRef();
    return new Promise<PhxReply<R>>((resolve, reject) => {
      const timeout = setTimeout(() => {
        this.pendingRefs.delete(ref);
        reject(new Error(`Timeout waiting for phx_reply for ${String(event)} ref=${ref}`));
      }, timeoutMs);

      this.pendingRefs.set(ref, (reply) => {
        clearTimeout(timeout);
        resolve(reply as PhxReply<R>);
      });

      this.push(event, payload, ref);
    });
  }

  /** Listen for events */
  on<E extends keyof (TEvents & SystemEventMap) & string>(
    event: E,
    handler: (payload: (TEvents & SystemEventMap)[E]) => void,
  ): () => void {
    if (!this.handlers.has(event)) {
      this.handlers.set(event, new Set());
    }
    this.handlers.get(event)!.add(handler as any);

    // Return unsubscribe function
    return () => {
      const set = this.handlers.get(event);
      if (!set) return;
      set.delete(handler as any);
      if (set.size === 0) this.handlers.delete(event);
    };
  }

  /** Handle incoming message (called by Socket) */
  handleMessage(msg: WireMessage): void {
    if (msg.event === "phx_reply" && msg.ref) {
      const callback = this.pendingRefs.get(msg.ref);
      if (callback) {
        callback(msg.payload as PhxReply<unknown>);
        this.pendingRefs.delete(msg.ref);
      }
      return;
    }

    const handlers = this.handlers.get(msg.event as any);
    if (handlers) {
      handlers.forEach((handler) => handler(msg.payload));
    }
  }

  private makeRef(): string {
    return `${++this.refCounter}`;
  }
}
